/**
 * FN-2b durable occurrence identity. DOM-free: browser and Worker share this
 * module; HTML parsing, asset canonicalization and rendered-state measurement
 * stay with their existing owners. See docs/finish-identity-repair-contract.md.
 *
 * createEditableIdRepairRecord receives canonical HTML identity hashes plus
 * transition hashes from hashEditableIdRepairFields(assetCanonicalSection).
 * Each occurrence includes BOTH full child-index paths and the full comment
 * path ({tag, ordinal, of}). A late anchor cannot reconstruct the latter from
 * child indices after the pre-repair DOM has disappeared on cold reload.
 *
 * classifyEditableIdRepairBatch is deliberately synchronous and UNVERIFIED.
 * verifyEditableIdRepairBatch must run at an async ingress before an Effects-
 * based synchronous collab core receives a trusted verification result. A
 * candidate is never permission to apply an operation. Verification covers
 * every record digest and each complete companion-field transition, including
 * absence. Callers must then guard the authoritative revision across awaits.
 *
 * Persistent HTML digests cover canonical asset HTML, never display URLs.
 * Raw bytes belong only to commit preconditions/history. An ingress whose
 * states are not already canonical MUST supply canonicalizeSection; this
 * module intentionally does not parse or reserialize HTML on the Worker.
 */
import {
  canonicalStringify,
  LIMITS,
  FORBIDDEN_PATH_SEGMENTS,
  isEditableIdRepairAbsentPatch
} from './collab-protocol.js';
import { sha256Hex } from './site-deploy.js';

export const EDITABLE_ID_REPAIR_VERSION = 1;
export const EDITABLE_ID_REPAIR_LIMITS = Object.freeze({
  records: 64,
  bindings: 500,
  pathDepth: 64,
  bytes: 128 * 1024
});
const HASH_RE = /^[0-9a-f]{64}$/;
const ID_RE = /^[A-Za-z0-9_-]{1,120}$/;
const TAG_RE = /^[a-z][a-z0-9:-]{0,79}$/;
const FIELDS = ['html', 'originalHtml', 'overrides'];
const COMPANIONS = [...FIELDS, 'editableIdRepairs'];
const RECORD_KEYS = [
  'id',
  'projectId',
  'instanceId',
  'beforeHtmlHash',
  'beforeOriginalHtmlHash',
  'afterHtmlHash',
  'afterOriginalHtmlHash',
  'previousRecordId',
  'occurrences',
  'commentBindings',
  'transition'
];
const OCCURRENCE_KEYS = [
  'fromEid',
  'toEid',
  'livePath',
  'originalPath',
  'commentPath',
  'signature',
  'omittedFirstMatchFields'
];
const OMITTABLE_FIELDS = new Set([
  'preserveAspectRatio',
  'imageTx',
  'imageTy',
  'imageScale',
  'imageHover',
  'imageEffect'
]);
const encoder = new TextEncoder();
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const same = (a, b) => canonicalStringify(a) === canonicalStringify(b);
const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const validId = (value) => typeof value === 'string' && ID_RE.test(value);
const validTag = (value) => typeof value === 'string' && TAG_RE.test(value);

function object(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
  );
}

function exactKeys(value, keys) {
  return (
    object(value) &&
    Object.keys(value).length === keys.length &&
    keys.every((key) => own(value, key))
  );
}

// Reject values JSON would silently discard/coerce, cycles, accessors and
// hostile keys before cloning/stringifying. Invalid metadata remains recoverable.
function jsonValue(value, ancestors = new Set(), depth = 0) {
  if (depth > 80) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (!Array.isArray(value) && !object(value)) return false;
  if (ancestors.has(value)) return false;
  ancestors.add(value);
  for (const key of Object.keys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (
      FORBIDDEN_PATH_SEGMENTS.includes(key) ||
      !own(descriptor, 'value') ||
      !jsonValue(descriptor.value, ancestors, depth + 1)
    ) {
      ancestors.delete(value);
      return false;
    }
  }
  ancestors.delete(value);
  return !Array.isArray(value) || Object.keys(value).length === value.length;
}

function validPath(path, comment = false) {
  return (
    Array.isArray(path) &&
    path.length > 0 &&
    path.length <= EDITABLE_ID_REPAIR_LIMITS.pathDepth &&
    path.every(
      (step) =>
        exactKeys(step, comment ? ['tag', 'ordinal', 'of'] : ['tag', 'index']) &&
        validTag(step.tag) &&
        (comment
          ? Number.isSafeInteger(step.ordinal) &&
            Number.isSafeInteger(step.of) &&
            step.ordinal >= 0 &&
            step.of > step.ordinal
          : Number.isSafeInteger(step.index) && step.index >= 0)
    )
  );
}

function validSignature(signature) {
  return (
    exactKeys(signature, ['tag', 'attrs', 'classes', 'textDigest']) &&
    validTag(signature.tag) &&
    object(signature.attrs) &&
    Object.entries(signature.attrs).every(
      ([key, value]) =>
        ['id', 'href', 'src', 'alt'].includes(key) &&
        typeof value === 'string' &&
        value.length <= 4096 &&
        !/^(?:blob:|data:)/i.test(value)
    ) &&
    Array.isArray(signature.classes) &&
    new Set(signature.classes).size === signature.classes.length &&
    signature.classes.every(
      (value) =>
        typeof value === 'string' &&
        value.length > 0 &&
        value.length <= 256 &&
        !/\s/.test(value) &&
        !value.startsWith('pb-')
    ) &&
    typeof signature.textDigest === 'string' &&
    signature.textDigest.length <= 120
  );
}

function pathCompare(a, b) {
  for (let i = 0; i < Math.min(a.length, b.length); i += 1) {
    const delta = a[i].index - b[i].index;
    if (delta) return delta;
  }
  return a.length - b.length;
}

function validTransition(transition) {
  return (
    exactKeys(transition, ['before', 'after']) &&
    ['before', 'after'].every(
      (side) =>
        exactKeys(
          transition[side],
          FIELDS.map((field) => `${field}Hash`)
        ) && FIELDS.every((field) => HASH_RE.test(transition[side][`${field}Hash`]))
    )
  );
}

function canonicalRecord(record) {
  const clone = structuredClone(record);
  clone.occurrences.forEach((occurrence) => {
    occurrence.signature.classes.sort(compare);
    occurrence.omittedFirstMatchFields.sort(compare);
  });
  clone.commentBindings.sort(
    (a, b) =>
      compare(a.threadId, b.threadId) ||
      compare(a.identityRevision || '', b.identityRevision || '') ||
      compare(a.anchorFingerprint, b.anchorFingerprint)
  );
  return JSON.parse(canonicalStringify(clone));
}

function recordProblem(record, previous, reserved) {
  if (
    !exactKeys(record, RECORD_KEYS) ||
    !HASH_RE.test(record.id) ||
    !(record.projectId === null || validId(record.projectId)) ||
    !validId(record.instanceId) ||
    !validTransition(record.transition)
  )
    return 'invalid-record';
  if (
    !['beforeHtmlHash', 'beforeOriginalHtmlHash', 'afterHtmlHash', 'afterOriginalHtmlHash'].every(
      (field) => HASH_RE.test(record[field])
    )
  )
    return 'invalid-html-hash';
  if (record.previousRecordId !== (previous?.id ?? null)) return 'invalid-chain';
  if (
    previous &&
    (record.instanceId !== previous.instanceId ||
      record.beforeOriginalHtmlHash !== previous.afterOriginalHtmlHash)
  )
    return 'invalid-lineage';
  if (
    !Array.isArray(record.occurrences) ||
    record.occurrences.length < 2 ||
    !Array.isArray(record.commentBindings) ||
    record.commentBindings.length > EDITABLE_ID_REPAIR_LIMITS.bindings
  ) {
    return 'invalid-occurrences-or-bindings';
  }
  const outputs = new Set();
  const groups = new Map();
  let lastPath = null;
  let renamed = false;
  for (const occurrence of record.occurrences) {
    if (
      !exactKeys(occurrence, OCCURRENCE_KEYS) ||
      !validId(occurrence.fromEid) ||
      !validId(occurrence.toEid) ||
      !validPath(occurrence.livePath) ||
      !validPath(occurrence.originalPath) ||
      !validPath(occurrence.commentPath, true) ||
      !validSignature(occurrence.signature) ||
      !same(occurrence.livePath, occurrence.originalPath) ||
      occurrence.commentPath.length !== occurrence.livePath.length ||
      occurrence.commentPath.some((step, i) => step.tag !== occurrence.livePath[i].tag) ||
      occurrence.signature.tag !== occurrence.livePath.at(-1).tag ||
      !Array.isArray(occurrence.omittedFirstMatchFields) ||
      new Set(occurrence.omittedFirstMatchFields).size !==
        occurrence.omittedFirstMatchFields.length ||
      !occurrence.omittedFirstMatchFields.every((field) => OMITTABLE_FIELDS.has(field))
    )
      return 'invalid-occurrence';
    if (
      outputs.has(occurrence.toEid) ||
      (lastPath && pathCompare(lastPath, occurrence.livePath) >= 0)
    ) {
      return 'occurrence-collision';
    }
    if (occurrence.toEid !== occurrence.fromEid) {
      if (!/^e\d+$/.test(occurrence.toEid) || reserved.has(occurrence.toEid)) return 'reserved-eid';
      renamed = true;
    }
    outputs.add(occurrence.toEid);
    lastPath = occurrence.livePath;
    const group = groups.get(occurrence.fromEid) || [];
    group.push(occurrence);
    groups.set(occurrence.fromEid, group);
  }
  for (const [eid, group] of groups) {
    if (
      group.length < 2 ||
      group[0].toEid !== eid ||
      new Set(group.map((occurrence) => canonicalStringify(occurrence.signature))).size !==
        group.length
    ) {
      return 'ambiguous-occurrence-group';
    }
    if (
      record.occurrences.some(
        (occurrence) => occurrence.fromEid !== eid && occurrence.toEid === eid
      )
    ) {
      return 'occurrence-collision';
    }
  }
  if (!renamed) return 'empty-repair';
  const bindings = new Set();
  for (const binding of record.commentBindings) {
    if (
      !exactKeys(binding, [
        'threadId',
        'identityRevision',
        'anchorFingerprint',
        'fromEid',
        'toEid'
      ]) ||
      !validId(binding.threadId) ||
      !(binding.identityRevision === null || validId(binding.identityRevision)) ||
      !HASH_RE.test(binding.anchorFingerprint) ||
      !record.occurrences.some(
        (occurrence) => occurrence.fromEid === binding.fromEid && occurrence.toEid === binding.toEid
      )
    )
      return 'invalid-binding';
    const key = canonicalStringify([
      binding.threadId,
      binding.identityRevision,
      binding.anchorFingerprint
    ]);
    if (bindings.has(key)) return 'binding-collision';
    bindings.add(key);
  }
  return null;
}

/** Device origin evidence is never reconstructed from absence of a cloud version. */
export function identityRepairNamespaceOf(state) {
  if (Number.isInteger(state?.cloudVersion) && state.cloudVersion >= 0) return 'cloud';
  if (state?.cloudVersion != null) return 'unknown';
  const origin = state?.identityRepairLocalOrigin;
  return origin?.version === 1 &&
    typeof state?.projectId === 'string' &&
    state.projectId &&
    origin.projectId === state.projectId &&
    Object.keys(origin).length === 2
    ? 'local'
    : 'unknown';
}

/** Normalize only supported records. Invalid present bytes remain recoverable. */
export function normalizeEditableIdRepairState(state) {
  const issues = [];
  for (const { path, section } of editableIdRepairSections(state)) {
    const normalized = normalizeEditableIdRepairs(section.editableIdRepairs, {
      instanceId: section.instanceId
    });
    if (!normalized.ok) issues.push({ path, reason: normalized.reason });
    else if (normalized.value === undefined) delete section.editableIdRepairs;
    else section.editableIdRepairs = normalized.value;
  }
  return issues;
}

/** Invalid values are returned untouched: loading must preserve recovery data. */
export function normalizeEditableIdRepairs(value, { instanceId } = {}) {
  if (value === undefined) return { ok: true, value: undefined };
  const refuse = (reason) => ({ ok: false, value, reason });
  if (
    !jsonValue(value) ||
    !exactKeys(value, ['version', 'records']) ||
    value.version !== EDITABLE_ID_REPAIR_VERSION ||
    !Array.isArray(value.records)
  )
    return refuse('unsupported-or-invalid-repair');
  if (
    value.records.length > EDITABLE_ID_REPAIR_LIMITS.records ||
    encoder.encode(canonicalStringify(value)).length > EDITABLE_ID_REPAIR_LIMITS.bytes
  )
    return refuse('repair-limit');
  const records = [];
  const ids = new Set();
  const reserved = new Set();
  for (const record of value.records) {
    const reason = recordProblem(record, records.at(-1), reserved);
    if (reason) return refuse(reason);
    if ((instanceId !== undefined && record.instanceId !== instanceId) || ids.has(record.id))
      return refuse('record-identity');
    ids.add(record.id);
    records.push(canonicalRecord(record));
    for (const occurrence of record.occurrences) {
      reserved.add(occurrence.fromEid);
      reserved.add(occurrence.toEid);
    }
  }
  return {
    ok: true,
    value: records.length ? { version: EDITABLE_ID_REPAIR_VERSION, records } : undefined
  };
}

/** SHA-256 over the canonical record excluding its own ID. */
export async function editableIdRepairRecordHashOf(record) {
  const value = canonicalRecord(record);
  delete value.id;
  return sha256Hex(canonicalStringify(value));
}

/** Async load/ingress check; synchronous normalization checks shape only. */
export async function verifyEditableIdRepairs(value, options) {
  const normalized = normalizeEditableIdRepairs(value, options);
  if (!normalized.ok) return normalized;
  for (const record of normalized.value?.records || []) {
    if ((await editableIdRepairRecordHashOf(record)) !== record.id) {
      return { ok: false, value, reason: 'repair-record-digest' };
    }
  }
  return normalized;
}

/** All input is planned before this call; no randomness or clock enters the ID. */
export async function createEditableIdRepairRecord(input) {
  if (!jsonValue(input)) throw new Error('Invalid editable identity repair input');
  const record = canonicalRecord({ ...input, id: '0'.repeat(64) });
  // Standalone creation validates its own shape. Chain/collision validation is
  // repeated when the caller appends the record to the actual retained history.
  const previous =
    record.previousRecordId === null
      ? null
      : {
          id: record.previousRecordId,
          instanceId: record.instanceId,
          afterOriginalHtmlHash: record.beforeOriginalHtmlHash
        };
  const reason = recordProblem(record, previous, new Set());
  if (reason) throw new Error(`Invalid editable identity repair: ${reason}`);
  record.id = await editableIdRepairRecordHashOf(record);
  return record;
}

/** Canonical HTML is caller-owned. Presence is distinct from a present null. */
export async function hashEditableIdRepairFields(canonicalSection) {
  const entries = await Promise.all(
    FIELDS.map(async (field) => {
      const present = own(canonicalSection, field) && canonicalSection[field] !== undefined;
      const value = present
        ? { present: true, value: canonicalSection[field] }
        : { present: false };
      if (!jsonValue(value)) throw new Error(`Invalid repair companion: ${field}`);
      // Element and property iteration order is CSS source order, including
      // competing picture-parent declarations. Sorting override keys would
      // authenticate a different rendered result as the same companion.
      const serialized = field === 'overrides' ? JSON.stringify(value) : canonicalStringify(value);
      return [`${field}Hash`, await sha256Hex(serialized)];
    })
  );
  return Object.fromEntries(entries);
}

/** Include unknown valid JSON fields; neither status nor updatedAt is identity. */
export async function commentAnchorFingerprint(anchorJson) {
  let anchor;
  try {
    anchor = typeof anchorJson === 'string' ? JSON.parse(anchorJson) : anchorJson;
  } catch {
    throw new Error('Invalid comment anchor JSON');
  }
  if (!object(anchor) || !jsonValue(anchor)) throw new Error('Invalid comment anchor JSON');
  return sha256Hex(canonicalStringify(anchor));
}

/** Reservations survive DOM deletion; unsupported metadata must not free IDs. */
export function reservedEditableIds(value) {
  const normalized = normalizeEditableIdRepairs(value);
  if (!normalized.ok) throw new Error(`Invalid editable identity repair: ${normalized.reason}`);
  return new Set(
    (normalized.value?.records || [])
      .flatMap((record) =>
        record.occurrences.flatMap((occurrence) => [occurrence.fromEid, occurrence.toEid])
      )
      .sort(compare)
  );
}

/** Present (including malformed) metadata requires the upgraded read/write path. */
export function editableIdRepairSections(state) {
  const entries = [];
  for (let pageIndex = 0; pageIndex < (state?.pages?.length || 0); pageIndex += 1) {
    const sections = state.pages[pageIndex]?.sections || [];
    for (let index = 0; index < sections.length; index += 1) {
      const section = sections[index];
      if (section?.editableIdRepairs !== undefined)
        entries.push({
          path: `pages.${pageIndex}.sections.${index}`,
          section
        });
    }
  }
  for (const key of ['navigation', 'footer']) {
    const section = state?.sharedSections?.[key];
    if (section?.editableIdRepairs !== undefined)
      entries.push({ path: `sharedSections.${key}`, section });
  }
  return entries;
}

/** Includes whole-section/page insertion payloads, not just direct field paths. */
export function patchesCarryEditableIdRepairs(patches) {
  const stack = [...(patches || [])];
  const seen = new Set();
  while (stack.length) {
    const value = stack.pop();
    if (!value || typeof value !== 'object' || seen.has(value)) continue;
    seen.add(value);
    if (own(value, 'editableIdRepairs')) return true;
    if (typeof value.path === 'string' && value.path.split('.').includes('editableIdRepairs'))
      return true;
    for (const child of Object.values(value))
      if (child && typeof child === 'object') stack.push(child);
  }
  return false;
}

function parseAnchor(anchorJson) {
  try {
    const anchor = typeof anchorJson === 'string' ? JSON.parse(anchorJson) : anchorJson;
    return object(anchor) &&
      jsonValue(anchor) &&
      anchor.version === 1 &&
      validId(anchor.eid) &&
      validSignature({
        tag: anchor.tag,
        attrs: anchor.attrs,
        classes: anchor.classes,
        textDigest: anchor.textDigest
      }) &&
      validPath(anchor.path, true) &&
      (anchor.identityRevision == null || validId(anchor.identityRevision)) &&
      (anchor.sourceSectionHash === undefined || HASH_RE.test(anchor.sourceSectionHash))
      ? structuredClone(anchor)
      : null;
  } catch {
    return null; // malformed persisted anchors are an explicit unresolved verdict
  }
}

function matchesOccurrence(anchor, occurrence) {
  return (
    anchor.eid === occurrence.fromEid &&
    same(anchor.path, occurrence.commentPath) &&
    same(
      {
        tag: anchor.tag,
        attrs: anchor.attrs,
        classes: [...(anchor.classes || [])].sort(compare),
        textDigest: anchor.textDigest
      },
      occurrence.signature
    )
  );
}

/**
 * Pure projection. Caller supplies fresh DOM-derived eidCounts, the canonical
 * reset-baseline hash, and the SHA-256 fingerprint of this exact stored anchor.
 * Loaded provenance must first pass verifyEditableIdRepairs at async ingress;
 * this synchronous function cannot establish SHA-256 integrity by itself.
 * An unresolved result MUST NOT fall through to the ordinary anchor resolver.
 */
export function projectCommentAnchor({
  section,
  projectId,
  thread,
  anchorFingerprint,
  currentOriginalHtmlHash,
  eidCounts
}) {
  const unresolved = (reason) => ({ kind: 'unresolved', reason });
  if (!thread?.anchorJson) return { kind: 'section', anchor: null, evidence: [] };
  const anchor = parseAnchor(thread.anchorJson);
  if (!anchor) return unresolved('unsupported-anchor');
  if (!section?.instanceId || thread.instanceId !== section.instanceId)
    return unresolved('section-mismatch');
  const normalized = normalizeEditableIdRepairs(section.editableIdRepairs, {
    instanceId: section.instanceId
  });
  if (!normalized.ok) return unresolved(normalized.reason);
  const records = normalized.value?.records || [];
  const count = (eid) => (eidCounts instanceof Map ? eidCounts.get(eid) : eidCounts?.[eid]);
  const evidence = [];
  const fingerprintValid = HASH_RE.test(anchorFingerprint);
  const revision = anchor.identityRevision ?? null;
  for (const record of records) {
    const affected = record.occurrences.some((occurrence) => occurrence.fromEid === anchor.eid);
    if (!affected) continue;
    if (record.projectId !== projectId) return unresolved('project-mismatch');
    const binding =
      fingerprintValid &&
      record.commentBindings.find(
        (candidate) =>
          candidate.threadId === thread.id &&
          candidate.identityRevision === revision &&
          candidate.anchorFingerprint === anchorFingerprint &&
          candidate.fromEid === anchor.eid
      );
    let target = binding?.toEid;
    let via = 'binding';
    if (!binding && anchor.sourceSectionHash === record.beforeHtmlHash) {
      const matches = record.occurrences.filter((occurrence) =>
        matchesOccurrence(anchor, occurrence)
      );
      if (matches.length !== 1) return unresolved('ambiguous-pre-repair-anchor');
      target = matches[0].toEid;
      via = 'source';
    } else if (!binding && !anchor.sourceSectionHash) {
      return unresolved('unbound-legacy-anchor');
    }
    if (target) {
      anchor.eid = target;
      evidence.push({ recordId: record.id, via, eid: target });
    }
  }
  if (evidence.length) {
    if (records.at(-1).afterOriginalHtmlHash !== currentOriginalHtmlHash)
      return unresolved('inactive-repair-lineage');
    if (count(anchor.eid) !== 1) return unresolved('missing-or-ambiguous-repaired-target');
    return { kind: 'projected', anchor, evidence };
  }
  if (revision !== null && count(anchor.eid) !== 1)
    return unresolved('missing-or-ambiguous-anchor-target');
  return { kind: 'unchanged', anchor, evidence };
}

function locateSection(state, path) {
  const pageMatch = /^pages\.(0|[1-9]\d*)\.sections\.(0|[1-9]\d*)$/.exec(path);
  if (pageMatch) {
    const page = state?.pages?.[Number(pageMatch[1])];
    const section = page?.sections?.[Number(pageMatch[2])];
    return page?.id && section?.instanceId
      ? { section, pageId: page.id, lock: `s:${section.instanceId}` }
      : null;
  }
  const sharedMatch = /^sharedSections\.(navigation|footer)$/.exec(path);
  const section = sharedMatch && state?.sharedSections?.[sharedMatch[1]];
  return section?.instanceId ? { section, pageId: '*', lock: `shared:${sharedMatch[1]}` } : null;
}

function transitionDirection(before, after) {
  const a = before?.records || [];
  const b = after?.records || [];
  if (Math.abs(a.length - b.length) !== 1) return null;
  const direction = b.length > a.length ? 'forward' : 'reverse';
  const shorter = direction === 'forward' ? a : b;
  const longer = direction === 'forward' ? b : a;
  return same(shorter, longer.slice(0, -1)) ? { direction, record: longer.at(-1) } : null;
}

/**
 * Returns none, invalid, or candidate with verified:false. Only an exact four-
 * field transition can become a candidate. Deep-path edits never qualify.
 */
export function classifyEditableIdRepairBatch(
  beforeState,
  patches,
  { expectedRepair = false, projectId = beforeState?.projectId ?? null } = {}
) {
  const invalid = (reason) => ({ kind: 'invalid', verified: false, reason });
  if (!Array.isArray(patches) || patches.length === 0)
    return expectedRepair ? invalid('missing-repair-patches') : { kind: 'none', verified: false };
  if (
    !patches.some(
      (patch) =>
        typeof patch?.path === 'string' && patch.path.split('.').includes('editableIdRepairs')
    )
  ) {
    return expectedRepair
      ? invalid('missing-repair-provenance')
      : { kind: 'none', verified: false };
  }
  const groups = new Map();
  for (const patch of patches) {
    if (
      patch?.op !== 'replace' ||
      typeof patch.path !== 'string' ||
      (patch.absent === true && !isEditableIdRepairAbsentPatch(patch)) ||
      (patch.value !== undefined && !jsonValue(patch.value))
    )
      return invalid('invalid-repair-patch');
    const keys = patch.path.split('.');
    const field = keys.pop();
    const path = keys.join('.');
    if (!COMPANIONS.includes(field)) return invalid('non-companion-patch');
    const located = locateSection(beforeState, path);
    if (!located) return invalid('unresolved-section');
    const group = groups.get(path) || { ...located, path, fields: new Map() };
    if (group.fields.has(field)) return invalid('duplicate-companion-patch');
    group.fields.set(field, patch.absent === true ? undefined : patch.value);
    groups.set(path, group);
  }
  const transitions = [];
  let direction;
  for (const group of groups.values()) {
    if (group.fields.size !== COMPANIONS.length) return invalid('missing-companion-patch');
    const before = normalizeEditableIdRepairs(group.section.editableIdRepairs, {
      instanceId: group.section.instanceId
    });
    const after = normalizeEditableIdRepairs(group.fields.get('editableIdRepairs'), {
      instanceId: group.section.instanceId
    });
    if (!before.ok || !after.ok) return invalid('invalid-repair-provenance');
    const transition = transitionDirection(before.value, after.value);
    if (!transition || (direction && direction !== transition.direction))
      return invalid('invalid-repair-direction');
    if (transition.record.projectId !== projectId) return invalid('repair-project-mismatch');
    direction = transition.direction;
    if (
      typeof group.fields.get('html') !== 'string' ||
      typeof group.fields.get('originalHtml') !== 'string'
    ) {
      return invalid('invalid-html-companion');
    }
    const overrides = group.fields.get('overrides');
    if (overrides !== undefined && !object(overrides))
      return invalid('invalid-overrides-companion');
    transitions.push({ path: group.path, recordId: transition.record.id });
  }
  return {
    kind: 'candidate',
    verified: false,
    direction,
    structural: true,
    pageId: '*',
    pageIds: [...new Set([...groups.values()].map((group) => group.pageId))].sort(compare),
    sections: [...new Set([...groups.values()].map((group) => group.lock))].sort(compare),
    transitions: transitions.sort((a, b) => compare(a.path, b.path))
  };
}

/**
 * Repeat classification against captured state, then verify all record and
 * companion digests. canonicalizeSection must return a canonical clone (or
 * mutate the supplied private clone); no caller-owned object is exposed to it.
 * The default is ONLY for already asset-canonical states, such as portable
 * snapshots. Enforce the full wire-frame byte cap at the actual transport too.
 */
export async function verifyEditableIdRepairBatch(
  beforeState,
  patches,
  {
    canonicalizeSection = (section) => section,
    expectedRepair = false,
    projectId = beforeState?.projectId ?? null
  } = {}
) {
  const candidate = classifyEditableIdRepairBatch(beforeState, patches, {
    expectedRepair,
    projectId
  });
  if (candidate.kind !== 'candidate') return candidate;
  const invalid = (reason) => ({ kind: 'invalid', verified: false, reason });
  if (encoder.encode(canonicalStringify(patches)).length > LIMITS.op.maxBytes)
    return invalid('repair-operation-limit');
  // Snapshot before the first await so caller mutation cannot change the
  // checked packet between hash operations. Authoritative revision is the
  // ingress owner's separate compare-and-commit responsibility.
  const capturedState = structuredClone(beforeState);
  const capturedPatches = structuredClone(patches);
  for (const transition of candidate.transitions) {
    const before = structuredClone(locateSection(capturedState, transition.path).section);
    const after = structuredClone(before);
    for (const patch of capturedPatches) {
      if (patch.path.startsWith(`${transition.path}.`)) {
        const field = patch.path.slice(transition.path.length + 1);
        if (patch.absent === true || patch.value === undefined) delete after[field];
        else after[field] = structuredClone(patch.value);
      }
    }
    const recordSide = candidate.direction === 'forward' ? after : before;
    const records = recordSide.editableIdRepairs.records;
    for (const record of records) {
      if ((await editableIdRepairRecordHashOf(record)) !== record.id)
        return invalid('repair-record-digest');
    }
    const record = records.at(-1);
    const canonicalBefore = await canonicalizeSection(before);
    const canonicalAfter = await canonicalizeSection(after);
    const beforeHashes = await hashEditableIdRepairFields(canonicalBefore);
    const afterHashes = await hashEditableIdRepairFields(canonicalAfter);
    const from = candidate.direction === 'forward' ? 'before' : 'after';
    const to = candidate.direction === 'forward' ? 'after' : 'before';
    if (!same(beforeHashes, record.transition[from]) || !same(afterHashes, record.transition[to])) {
      return invalid('repair-companion-digest');
    }
    const source = candidate.direction === 'forward' ? canonicalBefore : canonicalAfter;
    const target = candidate.direction === 'forward' ? canonicalAfter : canonicalBefore;
    if (
      (await sha256Hex(source.html)) !== record.beforeHtmlHash ||
      (await sha256Hex(source.originalHtml)) !== record.beforeOriginalHtmlHash ||
      (await sha256Hex(target.html)) !== record.afterHtmlHash ||
      (await sha256Hex(target.originalHtml)) !== record.afterOriginalHtmlHash
    )
      return invalid('repair-html-digest');
  }
  return { ...candidate, kind: 'repair', verified: true };
}
